import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/axios";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import MapPicker from "../components/MapPicker";
import { useToast } from "../components/ui/use-toast";

export default function AddProjectPage() {
    const navigate = useNavigate();
    const { toast } = useToast();
    
    // Default coordinates as requested
    const DEFAULT_LAT = 3.155488;
    const DEFAULT_LNG = 113.094029;
    
    const [project, setProject] = useState({
        name: "",
        status: "active",
        description: "",
        latitude: DEFAULT_LAT.toString(),
        longitude: DEFAULT_LNG.toString(),
    });
    
    const [location, setLocation] = useState({ 
        lat: DEFAULT_LAT, 
        lng: DEFAULT_LNG 
    });

    const statusOptions = [
        { value: "active", label: "Active" },
        { value: "completed", label: "Completed" },
        { value: "on_hold", label: "On Hold" },
        { value: "cancelled", label: "Cancelled" },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!project.name.trim()) {
            toast({
                title: "Validation Error",
                description: "Project name is required",
                variant: "destructive",
            });
            return;
        }
        if (!project.latitude.trim() || !project.longitude.trim()) {
            toast({
                title: "Validation Error",
                description: "Latitude and Longitude are required",
                variant: "destructive",
            });
            return;
        }
        
        try {
            const projectData = {
                ...project,
                latitude: parseFloat(project.latitude),
                longitude: parseFloat(project.longitude),
                description: project.description.trim() || null, // Allow blank description
            };
            
            await api.post("/api/projects/", projectData);
            toast({
                title: "Success",
                description: "Project created successfully",
            });
            navigate("/home");
        } catch (err) {
            console.error(err);
            let errorMessage = "Failed to create project";
            if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            } else if (err.response?.data?.name) {
                errorMessage = `Project name error: ${err.response.data.name.join(', ')}`;
            } else if (err.response?.data?.latitude) {
                errorMessage = `Latitude error: ${err.response.data.latitude.join(', ')}`;
            } else if (err.response?.data?.longitude) {
                errorMessage = `Longitude error: ${err.response.data.longitude.join(', ')}`;
            } else if (err.response?.data?.status) {
                errorMessage = `Status error: ${err.response.data.status.join(', ')}`;
            } else if (err.response?.data?.description) {
                errorMessage = `Description error: ${err.response.data.description.join(', ')}`;
            } else if (err.response?.status === 400) {
                errorMessage = "Invalid project data provided";
            } else if (err.response?.status === 403) {
                errorMessage = "You do not have permission to create projects";
            } else if (err.response?.status === 409) {
                errorMessage = "A project with this name already exists";
            }
            
            toast({
                title: "Project Creation Failed",
                description: errorMessage,
                variant: "destructive",
            });
        }
    };

    const handleMapLocationChange = (newLocation) => {
        setLocation(newLocation);
        setProject(prev => ({
            ...prev,
            latitude: newLocation.lat.toFixed(6),
            longitude: newLocation.lng.toFixed(6),
        }));
    };

    const handleCoordinateChange = (field, value) => {
        const numValue = parseFloat(value);
        setProject(prev => ({ ...prev, [field]: value }));
        
        if (!isNaN(numValue)) {
            if (field === 'latitude') {
                setLocation(prev => ({ ...prev, lat: numValue }));
            } else if (field === 'longitude') {
                setLocation(prev => ({ ...prev, lng: numValue }));
            }
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-6 space-y-6">
            <div className="text-center">
                <h1 className="text-3xl font-bold text-gray-900">Add New Project</h1>
                <p className="text-gray-600 mt-2">Create a new project with location and details</p>
            </div>
            
            <Card>
                <CardHeader>
                    <CardTitle className="text-xl">Project Information</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Project Name */}
                        <div>
                            <Label className="text-sm font-medium text-gray-700">
                                Project Name <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                value={project.name}
                                onChange={(e) => setProject({ ...project, name: e.target.value })}
                                placeholder="Enter project name"
                                className="mt-1"
                            />
                        </div>

                        {/* Status */}
                        <div>
                            <Label className="text-sm font-medium text-gray-700">
                                Status <span className="text-red-500">*</span>
                            </Label>
                            <Select 
                                value={project.status} 
                                onValueChange={(value) => setProject({ ...project, status: value })}
                            >
                                <SelectTrigger className="mt-1">
                                    <SelectValue placeholder="Select Status" />
                                </SelectTrigger>
                                <SelectContent>
                                    {statusOptions.map((status) => (
                                        <SelectItem key={status.value} value={status.value}>
                                            {status.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Description */}
                        <div>
                            <Label className="text-sm font-medium text-gray-700">
                                Description
                            </Label>
                            <textarea
                                value={project.description}
                                onChange={(e) => setProject({ ...project, description: e.target.value })}
                                placeholder="Enter project description (optional)"
                                className="mt-1 w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                rows={4}
                                maxLength={1000}
                            />
                            <div className="text-xs text-gray-500 mt-1">
                                {project.description.length}/1000 characters
                                {project.description.length > 900 && (
                                    <span className="ml-2 text-yellow-600 font-semibold">⚠️ Approaching limit!</span>
                                )}
                            </div>
                        </div>

                        {/* Coordinates Section */}
                        <div className="space-y-4">
                            <Label className="text-sm font-medium text-gray-700">
                                Location Coordinates <span className="text-red-500">*</span>
                            </Label>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-sm text-gray-600">Latitude</Label>
                                    <Input
                                        type="number"
                                        step="any"
                                        value={project.latitude}
                                        onChange={(e) => handleCoordinateChange('latitude', e.target.value)}
                                        placeholder="Enter latitude"
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-sm text-gray-600">Longitude</Label>
                                    <Input
                                        type="number"
                                        step="any"
                                        value={project.longitude}
                                        onChange={(e) => handleCoordinateChange('longitude', e.target.value)}
                                        placeholder="Enter longitude"
                                        className="mt-1"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Map Picker */}
                        <div>
                            <Label className="text-sm font-medium text-gray-700">
                                Map Location
                            </Label>
                            <div className="mt-2 p-4 border border-gray-200 rounded-md bg-gray-50">
                                <MapPicker
                                    location={location}
                                    setLocation={handleMapLocationChange}
                                />
                                <p className="text-xs text-gray-600 mt-2 text-center">
                                    💡 Click on the map to set coordinates, or manually enter them above
                                </p>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-4 justify-end pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate("/home")}
                            >
                                Cancel
                            </Button>
                            <Button 
                                type="submit" 
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                            >
                                Create Project
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
